'use client';

import { Shield, Award, Clock } from 'lucide-react';

interface TrustBadgesProps {
  /** Variant affects styling */
  variant?: 'light' | 'dark' | 'inline';
  /** Show partner logos */
  showPartners?: boolean;
  /** Show experience badge */
  showExperience?: boolean;
  /** Custom className */
  className?: string;
}

/**
 * Trust badges component showing partnerships and experience
 * Use near CTAs to increase conversion
 */
export default function TrustBadges({
  variant = 'light',
  showPartners = true,
  showExperience = true,
  className = '',
}: TrustBadgesProps) {
  const isLight = variant === 'light';
  const isDark = variant === 'dark';
  const isInline = variant === 'inline';

  const containerClasses = isInline
    ? `flex flex-wrap items-center justify-center gap-4 text-sm ${className}`
    : `rounded-lg p-4 ${isLight ? 'bg-gray-50 border border-gray-200' : 'bg-white/10 border border-white/20'} ${className}`;

  const textColor = isDark ? 'text-white/90' : 'text-gray-600';
  const mutedColor = isDark ? 'text-white/70' : 'text-gray-500';
  const accentColor = isDark ? 'text-white' : 'text-[#2813e8]';

  // Partner logos - using text for now, can add images later
  const partners = [
    { name: 'Microsoft CSP', highlight: true },
    { name: 'CrowdStrike', highlight: true },
    { name: 'Dell', highlight: false },
    { name: 'Pax8', highlight: false },
  ];

  if (isInline) {
    return (
      <div className={containerClasses}>
        {showExperience && (
          <span className={`flex items-center gap-1.5 ${textColor}`}>
            <Award className={`w-4 h-4 ${accentColor}`} />
            <span>25+ Years Experience</span>
          </span>
        )}
        {showPartners && (
          <>
            <span className={mutedColor}>•</span>
            <span className={`flex items-center gap-1.5 ${textColor}`}>
              <Shield className={`w-4 h-4 ${accentColor}`} />
              <span>Microsoft & CrowdStrike Partner</span>
            </span>
          </>
        )}
      </div>
    );
  }

  return (
    <div className={containerClasses}>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {/* Experience Badge */}
        {showExperience && (
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${isDark ? 'bg-white/20' : 'bg-[#2813e8]/10'}`}>
              <Award className={`w-5 h-5 ${accentColor}`} />
            </div>
            <div>
              <div className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                25+ Years Experience
              </div>
              <div className={`text-xs ${mutedColor}`}>
                Enterprise IT & Security
              </div>
            </div>
          </div>
        )}

        {/* Partner Badges */}
        {showPartners && (
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${isDark ? 'bg-white/20' : 'bg-[#2813e8]/10'}`}>
              <Shield className={`w-5 h-5 ${accentColor}`} />
            </div>
            <div>
              <div className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Authorized Partner
              </div>
              <div className={`text-xs ${mutedColor}`}>
                Microsoft • CrowdStrike • Drata & more
              </div>
            </div>
          </div>
        )}

        {/* Response Time */}
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-full ${isDark ? 'bg-white/20' : 'bg-[#2813e8]/10'}`}>
            <Clock className={`w-5 h-5 ${accentColor}`} />
          </div>
          <div>
            <div className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              Fast Response
            </div>
            <div className={`text-xs ${mutedColor}`}>
              Same business day
            </div>
          </div>
        </div>

        {/* CISSP-Certified */}
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-full ${isDark ? 'bg-white/20' : 'bg-[#2813e8]/10'}`}>
            <Award className={`w-5 h-5 ${accentColor}`} />
          </div>
          <div>
            <div className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              CISSP-Certified
            </div>
            <div className={`text-xs ${mutedColor}`}>
              Cybersecurity Leadership
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
